<template>
  <div class="profile-page">
    <div class="profile-card" v-if="user">
      <div class="profile-header">
        <div class="avatar">
          <img v-if="user.avatar_url" :src="user.avatar_url" alt="Avatar" />
          <span v-else class="avatar-placeholder">{{ initials }}</span>
        </div>
        <div class="profile-info">
          <h1>{{ user.name || user.email }}</h1>
          <p class="email">{{ user.email }}</p>
          <span class="provider-badge">{{ providerLabel }}</span>
        </div>
      </div>

      <!-- Subscription -->
      <div class="section-card">
        <h2>{{ $t('profile.subscription') }}</h2>
        <div class="sub-tier">
          <span class="tier-badge" :class="user.subscription">
            {{ user.subscription.toUpperCase() }}
          </span>
          <p class="tier-desc">{{ tierDescription }}</p>
        </div>
      </div>

      <!-- Edit Name -->
      <div class="section-card">
        <h2>{{ $t('profile.edit_profile') }}</h2>
        <form @submit.prevent="updateName" class="edit-form">
          <div class="form-row">
            <label>{{ $t('auth.name') }}</label>
            <input v-model="editName" type="text" class="form-input" />
          </div>
          <p v-if="updateMsg" class="success-msg">✅ {{ updateMsg }}</p>
          <button type="submit" class="save-btn" :disabled="saving">
            {{ saving ? '...' : $t('profile.save') }}
          </button>
        </form>
      </div>

      <!-- Account Info -->
      <div class="section-card">
        <h2>{{ $t('profile.account_info') }}</h2>
        <div class="info-grid">
          <div class="info-item">
            <span class="info-label">{{ $t('profile.member_since') }}</span>
            <span class="info-value">{{ formatDate(user.created) }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">{{ $t('profile.login_method') }}</span>
            <span class="info-value">{{ providerLabel }}</span>
          </div>
        </div>
      </div>

      <button @click="logout" class="logout-btn">{{ $t('profile.logout') }}</button>
    </div>

    <div v-else-if="loadError" class="loading error-state">
      <p>{{ loadError }}</p>
      <button class="retry-btn" @click="retryLoad">{{ $t('profile.retry') || 'Retry' }}</button>
    </div>
    <div v-else class="loading">Loading...</div>
  </div>
</template>

<script>
import { auth } from '../auth'

export default {
  name: 'ProfileView',
  data() {
    return {
      user: null,
      editName: '',
      saving: false,
      updateMsg: '',
      loadError: '',
    }
  },
  computed: {
    initials() {
      const name = this.user?.name || this.user?.email || ''
      return name.slice(0, 2).toUpperCase()
    },
    providerLabel() {
      const map = { email: 'Email', google: 'Google', facebook: 'Facebook' }
      return map[this.user?.provider] || this.user?.provider || ''
    },
    tierDescription() {
      const map = {
        free: 'Basic features included',
        pro: 'Pro features unlocked',
        enterprise: 'Enterprise-grade access',
      }
      return map[this.user?.subscription] || ''
    },
  },
  async mounted() {
    if (!auth.isLoggedIn()) {
      this.$router.push('/login')
      return
    }
    // Show cached user immediately while fetching
    const cached = auth.getUser()
    if (cached) {
      this.user = cached
      this.editName = cached.name || ''
    }
    try {
      const fresh = await auth.fetchProfile()
      if (fresh) {
        this.user = fresh
        this.editName = fresh.name || ''
      } else if (!cached) {
        // No cached user and fetch returned null → redirect to login
        this.$router.push('/login')
      } else {
        // Keep cached user, show warning
        this.loadError = ''
      }
    } catch (e) {
      if (!cached) {
        this.loadError = 'Failed to load profile. Please check your connection.'
      }
    }
  },
  methods: {
    async retryLoad() {
      this.loadError = ''
      try {
        const fresh = await auth.fetchProfile()
        if (fresh) {
          this.user = fresh
          this.editName = fresh.name || ''
        } else {
          this.loadError = 'Failed to load profile.'
        }
      } catch {
        this.loadError = 'Failed to load profile. Please check your connection.'
      }
    },
    async updateName() {
      this.saving = true
      this.updateMsg = ''
      try {
        this.user = await auth.updateProfile({ name: this.editName })
        this.updateMsg = 'Updated!'
        setTimeout(() => { this.updateMsg = '' }, 2000)
      } catch (e) {
        this.updateMsg = ''
      }
      this.saving = false
    },
    logout() {
      auth.logout()
      this.$router.push('/')
    },
    formatDate(d) {
      if (!d) return ''
      return new Date(d).toLocaleDateString()
    },
  },
}
</script>

<style scoped>
.profile-page {
  min-height: calc(100vh - 70px);
  display: flex;
  justify-content: center;
  padding: 40px 20px;
}

.profile-card {
  width: 100%;
  max-width: 560px;
  animation: fadeInUp 0.5s ease-out;
}

.profile-header {
  display: flex;
  align-items: center;
  gap: 20px;
  margin-bottom: 32px;
  padding: 24px;
  background: var(--surface0);
  border-radius: var(--radius);
  border: 1px solid var(--surface2);
}

.avatar img {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  object-fit: cover;
  border: 3px solid var(--blue);
}

.avatar-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--blue), var(--mauve));
  color: white;
  font-size: 24px;
  font-weight: 700;
}

.profile-info h1 {
  font-size: 22px;
  font-weight: 700;
  margin-bottom: 4px;
}

.email {
  color: var(--subtext0);
  font-size: 14px;
  margin-bottom: 6px;
}

.provider-badge {
  display: inline-block;
  font-size: 11px;
  font-weight: 600;
  padding: 3px 10px;
  border-radius: 20px;
  background: rgba(96, 144, 208, 0.15);
  color: var(--blue);
}

.section-card {
  background: var(--surface0);
  border: 1px solid var(--surface2);
  border-radius: var(--radius);
  padding: 20px 24px;
  margin-bottom: 16px;
}

.section-card h2 {
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 12px;
  color: var(--subtext1);
}

.sub-tier {
  display: flex;
  align-items: center;
  gap: 12px;
}

.tier-badge {
  display: inline-block;
  padding: 6px 16px;
  border-radius: var(--radius-xs);
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 1px;
}
.tier-badge.free {
  background: var(--surface1);
  color: var(--subtext1);
}
.tier-badge.pro {
  background: linear-gradient(135deg, var(--blue), var(--sapphire));
  color: white;
}
.tier-badge.enterprise {
  background: linear-gradient(135deg, var(--mauve), var(--pink));
  color: white;
}

.tier-desc {
  color: var(--subtext0);
  font-size: 13px;
}

.edit-form {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.form-row label {
  display: block;
  font-size: 13px;
  font-weight: 500;
  color: var(--subtext1);
  margin-bottom: 6px;
}

.form-input {
  width: 100%;
  padding: 10px 14px;
  border-radius: var(--radius-xs);
  border: 1px solid var(--surface2);
  background: var(--mantle);
  color: var(--text);
  font-size: 14px;
  font-family: inherit;
}
.form-input:focus {
  outline: none;
  border-color: var(--blue);
}

.save-btn {
  align-self: flex-end;
  padding: 8px 24px;
  border-radius: var(--radius-xs);
  background: var(--blue);
  color: white;
  font-size: 13px;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: all var(--transition);
}
.save-btn:hover { background: var(--sapphire); }

.success-msg {
  color: var(--green);
  font-size: 13px;
}

.info-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.info-label {
  display: block;
  font-size: 12px;
  color: var(--subtext0);
  margin-bottom: 4px;
}

.info-value {
  font-size: 14px;
  font-weight: 500;
}

.logout-btn {
  width: 100%;
  padding: 12px;
  border-radius: var(--radius-sm);
  background: var(--surface0);
  color: var(--red);
  border: 1px solid var(--surface2);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all var(--transition);
  margin-top: 8px;
}
.logout-btn:hover {
  background: var(--red);
  color: white;
  border-color: var(--red);
}

.loading {
  text-align: center;
  padding: 80px;
  color: var(--subtext0);
}

.error-state p {
  margin-bottom: 16px;
  color: var(--red, #e06c75);
}

.retry-btn {
  padding: 8px 24px;
  border-radius: var(--radius-xs);
  background: var(--surface1);
  color: var(--text);
  border: 1px solid var(--surface2);
  font-size: 14px;
  cursor: pointer;
  transition: all var(--transition);
}
.retry-btn:hover {
  background: var(--surface2);
}
</style>
